import React from 'react';
import { 
  Paper, Typography, Box, Divider, Grid, 
  LinearProgress, Chip, useTheme
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TranslateIcon from '@mui/icons-material/Translate';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import MemoryIcon from '@mui/icons-material/Memory';

const VLAMInsights = () => {
  const theme = useTheme();
  
  // Mock VLAM data
  const vlamData = {
    currentVisual: 'Large cardboard box with tape on top',
    currentInstruction: 'Remove the tape and open the flaps',
    predictions: [
      { action: 'Grip tape edge', confidence: 92 },
      { action: 'Pull tape upward', confidence: 88 },
      { action: 'Fold back flap', confidence: 76 },
      { action: 'Press down on box', confidence: 65 },
      { action: 'Lift box flap', confidence: 52 },
    ],
  };
  
  const getConfidenceColor = (confidence) => {
    if (confidence >= 85) return theme.palette.bmwAccent.midBlue;
    if (confidence >= 70) return '#FFC107';
    return '#F44336';
  };
  
  return (
    <Paper 
      elevation={10} 
      sx={{ 
        p: 0, 
        height: '100%',
        borderRadius: '16px',
        overflow: 'hidden',
        background: 'rgba(30, 30, 40, 0.9)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.05)',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255,255,255,0.05)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <MemoryIcon sx={{ color: theme.palette.bmwAccent.midBlue, mr: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            VLA-Model Insights
          </Typography>
        </Box>
      </Box>
      
      <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            p: 2, 
            borderRadius: '12px', 
            bgcolor: 'rgba(0, 0, 0, 0.2)',
            mb: 2
          }}>
            <VisibilityIcon 
              sx={{ 
                mr: 1.5, 
                color: theme.palette.bmwAccent.lightBlue,
                fontSize: 28
              }} 
            />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Visual Input
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {vlamData.currentVisual}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            p: 2, 
            borderRadius: '12px', 
            bgcolor: 'rgba(0, 0, 0, 0.2)'
          }}>
            <TranslateIcon 
              sx={{ 
                mr: 1.5, 
                color: theme.palette.bmwAccent.lightBlue,
                fontSize: 28
              }} 
            />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Language Understanding
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                "{vlamData.currentInstruction}"
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Divider sx={{ 
          my: 2, 
          opacity: 0.2,
          '&::before, &::after': {
            borderColor: 'rgba(255,255,255,0.1)',
          }
        }}>
          <Chip 
            label="PREDICTIONS" 
            size="small" 
            sx={{ 
              bgcolor: 'rgba(0, 0, 0, 0.4)', 
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.7rem'
            }} 
          />
        </Divider>
        
        <Box sx={{ 
          flex: 1, 
          borderRadius: '12px', 
          bgcolor: 'rgba(0, 0, 0, 0.2)',
          p: 2
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 2
          }}>
            <TouchAppIcon 
              sx={{ 
                mr: 1.5, 
                color: theme.palette.bmwAccent.lightBlue,
                fontSize: 24
              }} 
            />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Action Predictions
            </Typography>
          </Box>
          
          <Box sx={{ overflow: 'auto', pr: 1, pl: 0.5 }}>
            {vlamData.predictions.map((pred, index) => (
              <Box 
                key={index} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 1.5,
                  p: 1.5,
                  borderRadius: '8px',
                  bgcolor: 'rgba(0, 0, 0, 0.3)',
                  border: `1px solid rgba(${
                    pred.confidence >= 85 ? '33, 150, 243' : 
                    pred.confidence >= 70 ? '255, 193, 7' : 
                    '244, 67, 54'
                  }, 0.2)`,
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'translateX(5px)',
                    bgcolor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    minWidth: 120, 
                    flexShrink: 0,
                    fontWeight: 600
                  }}
                >
                  {pred.action}
                </Typography>
                <Box sx={{ flex: 1, mx: 1.5 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={pred.confidence} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        background: `linear-gradient(90deg, ${getConfidenceColor(pred.confidence)} 0%, rgba(${
                          pred.confidence >= 85 ? '33, 150, 243' : 
                          pred.confidence >= 70 ? '255, 193, 7' : 
                          '244, 67, 54'
                        }, 0.7) 100%)`,
                        borderRadius: 4
                      }
                    }}
                  />
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    flexShrink: 0, 
                    width: 35, 
                    textAlign: 'right',
                    fontWeight: 700,
                    color: pred.confidence >= 85 ? theme.palette.bmwAccent.lightBlue : 
                           pred.confidence >= 70 ? '#FFC107' : 
                           '#F44336'
                  }}
                >
                  {pred.confidence}%
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default VLAMInsights; 